import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiSearch, FiRefreshCw, FiDownload, FiTruck, FiAlertTriangle, FiCheckCircle, FiClock, FiChevronDown, FiChevronUp, FiPlay, FiInfo,
} from 'react-icons/fi';
import css from './ConsultaNfs.module.css';
import cx from './ConsultaNfe.module.css';
import {
  listarClientesCte, listarCtes, listarExecucoesCte, sincronizarClienteCte, ativarBuscaCte, executarCapturaCteTodos, baixarCte, urlZipCte,
} from '../../services/cteCaptura';
import { ehPortal } from '../../utils/portal';

/* ── Consulta CT-e ──
   Conhecimentos de transporte (CT-e e CT-e OS) em que o cliente aparece como
   tomador, remetente, destinatário, expedidor ou recebedor, pela Distribuição
   de DF-e do CT-e (SEFAZ, Ambiente Nacional) com o A1 do cliente. O CT-e já
   vem completo. Os emitidos pelo próprio cliente não vêm por este serviço. */

const STATUS = {
  nunca: ['Ainda não sincronizado', css.stNeutro], rodando: ['Sincronizando...', css.stRodando], ok: ['Em dia', css.stOk],
  erro: ['Erro na última consulta', css.stErro], limite: ['Consumo indevido (SEFAZ 656): nova consulta em 1h', css.stAlerta],
  sem_certificado: ['Sem certificado', css.stErro], certificado_vencido: ['Certificado vencido', css.stErro], senha_invalida: ['Senha do certificado inválida', css.stErro],
  sem_uf: ['Cadastro sem UF/município', css.stErro],
};
const PENDENTES = ['erro', 'limite', 'sem_certificado', 'certificado_vencido', 'senha_invalida', 'sem_uf'];
const PAPEL = { tomador: 'Tomador', remetente: 'Remetente', destinatario: 'Destinatário', expedidor: 'Expedidor', recebedor: 'Recebedor' };
const moeda = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—');
const quando = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const hora = (iso) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
const doc = (d) => {
  const x = String(d || '').replace(/\D/g, '');
  if (x.length === 14) return x.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (x.length === 11) return x.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return d || '';
};
const mesAtual = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

function ConsultaCte() {
  const [dados, setDados] = useState({ items: [], ambiente: 2 });
  const [verPendencias, setVerPendencias] = useState(false);
  const [busca, setBusca] = useState('');
  const [sel, setSel] = useState(null);
  const [modoData, setModoData] = useState('mes');
  const [filtro, setFiltro] = useState({ competencia: mesAtual(), inicio: '', fim: '', papel: '', situacao: 'ativa' });
  const [docs, setDocs] = useState({ items: [], totais: { quantidade: 0, valor: 0, icms: 0, tomados: 0 } });
  const [carregando, setCarregando] = useState(false);
  const [execucoes, setExecucoes] = useState([]);
  const [verLog, setVerLog] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const sincronizandoAntes = useRef(false);

  const carregarClientes = useCallback(() => listarClientesCte().then(setDados).catch(e => setErro(e.message)), []);
  useEffect(() => { carregarClientes(); }, [carregarClientes]);

  const cliente = dados.items.find(c => c.id === sel) || null;
  const filtroApi = sel ? {
    cliente_id: sel, papel: filtro.papel, situacao: filtro.situacao,
    ...(modoData === 'mes' ? { competencia: filtro.competencia } : { inicio: filtro.inicio, fim: filtro.fim }),
  } : null;
  const chaveFiltro = JSON.stringify(filtroApi);

  const carregarDocs = useCallback(() => {
    if (!filtroApi) return Promise.resolve();
    setCarregando(true); setErro('');
    return listarCtes(filtroApi).then(setDocs).catch(e => setErro(e.message)).finally(() => setCarregando(false));
  }, [chaveFiltro]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregarDocs(); }, [carregarDocs]);
  useEffect(() => { if (sel) listarExecucoesCte(sel).then(r => setExecucoes(r.items || [])).catch(() => {}); }, [sel, docs]);

  const sincronizando = !!cliente?.sincronizando || dados.jobRodando;
  useEffect(() => {
    if (!sincronizando) {
      if (sincronizandoAntes.current) { sincronizandoAntes.current = false; carregarDocs(); }
      return undefined;
    }
    sincronizandoAntes.current = true;
    const t = setInterval(carregarClientes, 4000);
    return () => clearInterval(t);
  }, [sincronizando, carregarClientes, carregarDocs]);

  const bloqueadoAte = cliente?.proxima_consulta && new Date(cliente.proxima_consulta) > new Date() ? cliente.proxima_consulta : null;

  async function sincronizar() {
    setErro(''); setMsg('');
    try { await sincronizarClienteCte(sel); setMsg('Sincronização iniciada. A primeira carga traz os últimos 3 meses.'); await carregarClientes(); }
    catch (e) { setErro(e.message); }
  }
  async function alternarBusca() {
    const ligar = !cliente.busca_ativa;
    if (!window.confirm(ligar
      ? `Ativar a busca de CT-e deste cliente no nosso sistema?\n\nSó ative se ele NÃO for mais buscado pelo SIEG (ou outro sistema): a SEFAZ tem uma fila só por CNPJ e os dois sistemas perdem documentos um para o outro.`
      : `Desativar a busca de CT-e deste cliente? Ele sai do job da madrugada e o botão de sincronizar fica bloqueado.`)) return;
    setErro(''); setMsg('');
    try { await ativarBuscaCte(cliente.id, ligar); setMsg(ligar ? 'Busca ativada. Use "Sincronizar agora" ou aguarde o job da madrugada.' : 'Busca desativada.'); await carregarClientes(); }
    catch (e) { setErro(e.message); }
  }
  async function capturarTodos() {
    if (!window.confirm('Capturar agora os CT-e de todos os clientes com certificado? Roda em segundo plano, um cliente por vez; quem consultou há menos de 1h é pulado (regra da SEFAZ).')) return;
    setErro(''); setMsg('');
    try { await executarCapturaCteTodos(); setMsg('Captura de todos iniciada em segundo plano.'); carregarClientes(); } catch (e) { setErro(e.message); }
  }
  async function baixar(caminho, nome) {
    setErro('');
    try { await baixarCte(caminho, nome); } catch (e) { setErro(e.message); }
  }

  /* usuário de cliente: a lista é só ele -- já entra selecionado e sem a
     barra lateral de clientes, com a tela inteira pras notas */
  const portal = ehPortal();
  useEffect(() => {
    if (portal && !sel && dados.items[0]) setSel(dados.items[0].id);
  }, [portal, sel, dados.items]);

  const q = busca.trim().toLowerCase();
  const lista = dados.items.filter(c => !q || `${c.codigo} ${c.nome}`.toLowerCase().includes(q));
  const pendencias = dados.items.filter(c => PENDENTES.includes(c.status));
  const [stTexto, stCls] = STATUS[cliente?.sincronizando ? 'rodando' : (cliente?.status || 'nunca')] || STATUS.nunca;

  return (
    <div className={css.page}>
      <div className={css.header}>
        <div className={css.headerIcone}><FiTruck size={20} /></div>
        <div>
          <h1 className={css.titulo}>Consulta CT-e</h1>
          <p className={css.sub}>Conhecimentos de transporte (CT-e e CT-e OS) dos clientes, pela Distribuição de DF-e da SEFAZ com o certificado A1 de cada cliente</p>
        </div>
        <span className={dados.ambiente === 1 ? css.ambProd : css.ambHom}>{dados.ambiente === 1 ? 'Produção' : 'Homologação'}</span>
        {!ehPortal() && (
        <button type="button" className={css.btnSec} onClick={capturarTodos} disabled={dados.jobRodando}>
          <FiPlay size={13} /> {dados.jobRodando ? 'Capturando todos...' : 'Capturar todos agora'}
        </button>
        )}
      </div>

      <div className={cx.aviso}>
        <FiInfo size={14} />
        <span>Vêm os CT-e em que o cliente é <strong>tomador</strong> (paga o frete), remetente, destinatário, expedidor ou recebedor, já com o XML completo. A SEFAZ guarda só os <strong>últimos 3 meses</strong> e libera nova consulta de cada CNPJ <strong>1 hora</strong> depois de esgotar a fila. CT-e emitidos pelo próprio cliente (transportadora) não vêm por este serviço.</span>
      </div>

      {pendencias.length > 0 && (
        <div className={css.pendencias}>
          <button type="button" onClick={() => setVerPendencias(v => !v)}>
            <FiAlertTriangle size={14} /> {pendencias.length} cliente(s) com pendência na captura (certificado, cadastro ou erro)
            {verPendencias ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
          </button>
          {verPendencias && (
            <ul>
              {pendencias.map(p => (
                <li key={p.id}><strong>{p.codigo} - {p.nome}</strong>: {(STATUS[p.status] || [p.status])[0]}{p.ultimo_erro ? ` — ${p.ultimo_erro}` : ''}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {erro && <p className={css.erro}>{erro}</p>}
      {msg && <p className={css.ok}>{msg}</p>}

      <div className={css.corpo} style={portal ? { gridTemplateColumns: 'minmax(0, 1fr)' } : undefined}>
        {!portal && (
        <aside className={css.lista}>
          <div className={css.busca}><FiSearch size={13} /><input placeholder="Buscar cliente..." value={busca} onChange={e => setBusca(e.target.value)} /></div>
          <p className={css.listaInfo}>{dados.items.length} cliente(s) com certificado</p>
          {lista.map(c => {
            const [, cls] = STATUS[c.sincronizando ? 'rodando' : (c.status || 'nunca')] || STATUS.nunca;
            return (
              <button key={c.id} type="button" className={`${css.itemCliente} ${sel === c.id ? css.itemAtivo : ''}`} onClick={() => setSel(c.id)}>
                <span className={`${css.ponto} ${cls}`} />
                <span className={css.itemTexto}>
                  <strong>{c.codigo} - {c.nome}</strong>
                  <small>{!c.busca_ativa && <span className={cx.tagDesativada}>DESATIVADA</span>}{c.documentos} CT-e · {c.ultima_sincronizacao ? quando(c.ultima_sincronizacao) : 'nunca sincronizado'}</small>
                </span>
              </button>
            );
          })}
          {!lista.length && <p className={css.vazio}>Nenhum cliente com certificado A1 cadastrado no menu Certificados.</p>}
        </aside>
        )}

        <section className={css.detalhe}>
          {!cliente ? (
            <div className={css.placeholder}><FiTruck size={34} /><p>Escolha um cliente para ver os CT-e.</p></div>
          ) : (
            <>
              <div className={css.cliTopo}>
                <div>
                  <h2>{cliente.codigo} - {cliente.nome}</h2>
                  <p>{doc(cliente.cnpj)} · certificado vence {cliente.certificado_vence ? cliente.certificado_vence.split('-').reverse().join('/') : '—'} · NSU {cliente.ultimo_nsu ?? 0}{cliente.max_nsu ? ` de ${cliente.max_nsu}` : ''}</p>
                  {!ehPortal() && (
                  <label className={cx.chaveBusca} title="A SEFAZ tem uma fila só por CNPJ: ative só quando o SIEG (ou outro sistema) parar de buscar este cliente">
                    <input type="checkbox" checked={!!cliente.busca_ativa} onChange={alternarBusca} />
                    <span className={cx.chaveTrilho}><span /></span>
                    {cliente.busca_ativa ? 'Busca ativa no nosso sistema' : 'Busca desativada (ainda no SIEG)'}
                  </label>
                  )}
                  <span className={`${css.status} ${stCls}`}>{stTexto}{cliente.status !== 'ok' && cliente.ultimo_erro ? `: ${cliente.ultimo_erro}` : ''}</span>
                </div>
                <div className={cx.acoesTopo}>
                  {!ehPortal() && (
                  <button type="button" className={css.btnPri} onClick={sincronizar} disabled={sincronizando || !!bloqueadoAte || !cliente.busca_ativa}>
                    <FiRefreshCw size={13} className={sincronizando ? css.girando : ''} /> {sincronizando ? 'Sincronizando...' : 'Sincronizar agora'}
                  </button>
                  )}
                  {bloqueadoAte && !sincronizando && <small className={cx.bloqueio}><FiClock size={11} /> SEFAZ libera nova consulta às {hora(bloqueadoAte)}</small>}
                </div>
              </div>

              <div className={css.filtros}>
                <div className={css.segmento}>
                  <button type="button" className={modoData === 'mes' ? css.segAtivo : ''} onClick={() => setModoData('mes')}>Mês</button>
                  <button type="button" className={modoData === 'periodo' ? css.segAtivo : ''} onClick={() => setModoData('periodo')}>Período</button>
                </div>
                {modoData === 'mes' ? (
                  <input type="month" className={css.input} value={filtro.competencia} onChange={e => setFiltro(f => ({ ...f, competencia: e.target.value }))} title="Mês da emissão" />
                ) : (
                  <>
                    <input type="date" className={css.input} value={filtro.inicio} onChange={e => setFiltro(f => ({ ...f, inicio: e.target.value }))} title="Data inicial (emissão)" />
                    <span className={css.ate}>até</span>
                    <input type="date" className={css.input} value={filtro.fim} onChange={e => setFiltro(f => ({ ...f, fim: e.target.value }))} title="Data final (emissão)" />
                  </>
                )}
                <select className={css.input} value={filtro.papel} onChange={e => setFiltro(f => ({ ...f, papel: e.target.value }))}>
                  <option value="">Todos os CT-e</option>
                  <option value="tomador">Cliente é o tomador</option>
                  <option value="outros">Cliente não é o tomador</option>
                </select>
                <select className={css.input} value={filtro.situacao} onChange={e => setFiltro(f => ({ ...f, situacao: e.target.value }))}>
                  <option value="ativa">Autorizados</option>
                  <option value="cancelada">Cancelados</option>
                  <option value="todas">Todos</option>
                </select>
                <span className={css.espaco} />
                <button type="button" className={css.btnPri} disabled={!docs.items.length} title="XML em pastas tomados/ e outros/" onClick={() => baixar(urlZipCte(filtroApi), 'cte.zip')}>
                  <FiDownload size={13} /> Baixar ZIP
                </button>
              </div>

              <div className={cx.totais}>
                <div><span>CT-e</span><strong>{docs.totais.quantidade}</strong></div>
                <div><span>Valor das prestações</span><strong>{moeda(docs.totais.valor)}</strong></div>
                <div><span>ICMS</span><strong>{moeda(docs.totais.icms)}</strong></div>
                <div><span>Cliente tomador</span><strong>{docs.totais.tomados}</strong></div>
              </div>

              <div className={css.tabelaWrap}>
                <table className={css.tabela}>
                  <thead>
                    <tr><th>Emissão</th><th>Nº / Série</th><th>Papel do cliente</th><th>Transportadora</th><th>Tomador</th><th>Trajeto</th><th className={css.num}>Valor</th><th className={css.num}>ICMS</th><th>Situação</th><th /></tr>
                  </thead>
                  <tbody>
                    {carregando ? <tr><td colSpan={10} className={css.vazio}>Carregando...</td></tr>
                      : !docs.items.length ? <tr><td colSpan={10} className={css.vazio}>Nenhum CT-e nesse filtro.</td></tr>
                        : docs.items.map(n => (
                          <tr key={n.id} className={n.situacao !== 'autorizado' ? css.linhaCancelada : ''}>
                            <td>{dataBr(n.data_emissao)}</td>
                            <td>{n.numero}{n.serie ? ` / ${n.serie}` : ''}{n.modelo === '67' ? <em className={cx.manif}>CT-e OS</em> : null}</td>
                            <td>{(n.papeis?.length ? n.papeis : ['—']).map(p => (
                              <span key={p} className={p === 'tomador' ? css.tEmitida : css.tRecebida} style={{ marginRight: 3 }}>{PAPEL[p] || p}</span>
                            ))}</td>
                            <td className={css.contra}>{n.emitente_nome || '—'}<em>{doc(n.emitente_doc)}</em></td>
                            <td className={css.contra}>{n.tomador_nome || '—'}<em>{doc(n.tomador_doc)}</em></td>
                            <td>{n.uf_inicio && n.uf_fim ? `${n.uf_inicio} → ${n.uf_fim}` : '—'}{n.qtd_nfe ? <em className={cx.manif}>{n.qtd_nfe} NF-e</em> : null}</td>
                            <td className={css.num}>{moeda(n.valor_prestacao)}</td>
                            <td className={css.num}>{moeda(n.valor_icms)}</td>
                            <td>{n.situacao === 'autorizado' ? <span className={css.sAtiva}><FiCheckCircle size={11} /> Autorizado</span> : <span className={css.sCancelada}>Cancelado</span>}</td>
                            <td className={css.acoes}><button type="button" title="Baixar XML" onClick={() => baixar(`/documentos/${n.id}/xml`, `${n.chave_acesso}.xml`)}>XML</button></td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>

              <div className={css.log}>
                <button type="button" onClick={() => setVerLog(v => !v)}><FiClock size={13} /> Execuções deste cliente {verLog ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}</button>
                {verLog && (
                  <table className={css.tabela}>
                    <thead><tr><th>Início</th><th>Origem</th><th>CT-e novos</th><th>Eventos</th><th>Erros</th><th>Duração</th><th>Mensagem</th></tr></thead>
                    <tbody>
                      {execucoes.map(e => (
                        <tr key={e.id}>
                          <td>{quando(e.inicio)}</td><td>{e.origem}</td><td>{e.documentos}</td><td>{e.eventos}</td><td>{e.erros}</td>
                          <td>{e.duracao_ms != null ? `${Math.round(e.duracao_ms / 1000)}s` : '…'}</td><td className={css.msgLog}>{e.mensagem || '—'}</td>
                        </tr>
                      ))}
                      {!execucoes.length && <tr><td colSpan={7} className={css.vazio}>Nenhuma execução ainda.</td></tr>}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default ConsultaCte;
